import React, { useState, useEffect } from 'react';
import { 
  Activity,
  AlertCircle,
  CheckCircle,
  XCircle,
  TrendingUp,
  TrendingDown,
  Server,
  Percent,
  Clock
} from 'lucide-react';
import { apiClient } from '../../services/api';

interface HealthCheck {
  status: string;
  orphan_count?: number;
  oldest_orphan_hours?: number;
  estimated_cost?: number;
  success_rate?: number;
  destroyed?: number;
  failed?: number;
  active_runs?: number;
  alerts?: Alert[];
}

interface Alert {
  type: string;
  severity: 'info' | 'warning' | 'critical';
  message: string;
  value?: any;
}

interface HealthReport {
  overall_status: 'healthy' | 'warning' | 'critical' | 'error';
  total_alerts: number;
  critical_alerts: number;
  warning_alerts: number;
  checks: {
    orphans?: HealthCheck;
    destruction_rate?: HealthCheck;
    active_runs?: HealthCheck;
  };
  alerts?: Alert[];
  timestamp: string;
}

export const SystemHealthWidget: React.FC = () => {
  const [healthReport, setHealthReport] = useState<HealthReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchHealthReport = async () => {
    try {
      const response = await apiClient.get('/admin/monitoring/health-report');
      setHealthReport(response); // apiClient already returns the data
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch health report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealthReport();
    
    if (autoRefresh) {
      const interval = setInterval(fetchHealthReport, 60000); // Refresh every minute
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'warning':
        return <AlertCircle className="h-5 w-5 text-yellow-500" />;
      case 'critical':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'bg-green-900/20 border-green-700';
      case 'warning':
        return 'bg-yellow-900/20 border-yellow-700';
      case 'critical':
        return 'bg-red-900/20 border-red-700';
      default:
        return 'bg-gray-800 border-gray-700';
    }
  };

  const formatPercentage = (value?: number) => {
    if (value === undefined) return 'N/A';
    return `${(value * 100).toFixed(1)}%`;
  };

  if (loading) {
    return (
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-700 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-3 bg-gray-700 rounded"></div>
            <div className="h-3 bg-gray-700 rounded"></div>
            <div className="h-3 bg-gray-700 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
        <div className="flex items-center text-red-400">
          <XCircle className="h-5 w-5 mr-2" />
          <span className="text-sm">{error}</span>
        </div>
      </div>
    );
  }

  if (!healthReport) {
    return null;
  }

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-700">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <Activity className="h-5 w-5 text-gray-400 mr-2" />
            <h3 className="text-lg font-semibold text-white">System Health</h3>
          </div>
          <div className="flex items-center space-x-2">
            <label className="flex items-center text-sm text-gray-400">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="h-3 w-3 text-blue-600 rounded border-gray-300 mr-1"
              />
              Auto-refresh
            </label>
            <div className={`px-3 py-1 rounded-full text-xs font-medium ${
              healthReport.overall_status === 'healthy' 
                ? 'bg-green-100 text-green-800'
                : healthReport.overall_status === 'warning'
                ? 'bg-yellow-100 text-yellow-800'
                : 'bg-red-100 text-red-800'
            }`}>
              {healthReport.overall_status.toUpperCase()}
            </div>
          </div>
        </div>
      </div>

      {/* Alert Summary */}
      {healthReport.total_alerts > 0 && (
        <div className={`px-6 py-3 border-b ${getStatusColor(healthReport.overall_status)}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {getStatusIcon(healthReport.overall_status)}
              <span className="ml-2 text-sm font-medium text-gray-100">
                {healthReport.total_alerts} Active Alert{healthReport.total_alerts !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="flex space-x-3 text-xs">
              {healthReport.critical_alerts > 0 && (
                <span className="text-red-400">
                  {healthReport.critical_alerts} Critical
                </span>
              )}
              {healthReport.warning_alerts > 0 && (
                <span className="text-yellow-400">
                  {healthReport.warning_alerts} Warning
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Health Metrics */}
      <div className="px-6 py-4 space-y-4">
        {/* Orphaned Droplets Check */}
        {healthReport.checks.orphans && (
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Server className="h-4 w-4 text-gray-400 mr-2" />
              <div>
                <p className="text-sm font-medium text-white">Orphaned Droplets</p>
                <p className="text-xs text-gray-400">
                  {healthReport.checks.orphans.orphan_count || 0} found
                  {healthReport.checks.orphans.estimated_cost && 
                    ` ($${healthReport.checks.orphans.estimated_cost.toFixed(2)} cost)`}
                </p>
              </div>
            </div>
            <div className="flex items-center">
              {getStatusIcon(healthReport.checks.orphans.status)}
            </div>
          </div>
        )}

        {/* Destruction Success Rate */}
        {healthReport.checks.destruction_rate && (
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Percent className="h-4 w-4 text-gray-400 mr-2" />
              <div>
                <p className="text-sm font-medium text-white">Destruction Success</p>
                <p className="text-xs text-gray-400">
                  {formatPercentage(healthReport.checks.destruction_rate.success_rate)}
                  {healthReport.checks.destruction_rate.destroyed !== undefined && 
                    ` (${healthReport.checks.destruction_rate.destroyed}/${
                      healthReport.checks.destruction_rate.destroyed + 
                      (healthReport.checks.destruction_rate.failed || 0)
                    })`}
                </p>
              </div>
            </div>
            <div className="flex items-center">
              {healthReport.checks.destruction_rate.success_rate && 
               healthReport.checks.destruction_rate.success_rate < 0.95 ? (
                <TrendingDown className="h-4 w-4 text-red-500 mr-1" />
              ) : (
                <TrendingUp className="h-4 w-4 text-green-500 mr-1" />
              )}
              {getStatusIcon(healthReport.checks.destruction_rate.status)}
            </div>
          </div>
        )}

        {/* Active Runs */}
        {healthReport.checks.active_runs && (
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Activity className="h-4 w-4 text-gray-400 mr-2" />
              <div>
                <p className="text-sm font-medium text-white">Active Runs</p>
                <p className="text-xs text-gray-400">
                  {healthReport.checks.active_runs.active_runs || 0} running
                </p>
              </div>
            </div>
            <div className="flex items-center">
              {getStatusIcon(healthReport.checks.active_runs.status)}
            </div>
          </div>
        )}
      </div>

      {/* Active Alerts */}
      {healthReport.alerts && healthReport.alerts.length > 0 && (
        <div className="px-6 py-3 border-t border-gray-700 bg-gray-900">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
            Active Alerts
          </p>
          <div className="space-y-1">
            {healthReport.alerts.slice(0, 3).map((alert, index) => (
              <div key={index} className="flex items-start">
                <div className={`h-2 w-2 rounded-full mt-1 mr-2 ${
                  alert.severity === 'critical' ? 'bg-red-500' :
                  alert.severity === 'warning' ? 'bg-yellow-500' :
                  'bg-blue-500'
                }`} />
                <p className="text-xs text-gray-300 leading-tight">{alert.message}</p>
              </div>
            ))}
            {healthReport.alerts.length > 3 && (
              <p className="text-xs text-gray-400 italic">
                +{healthReport.alerts.length - 3} more alerts
              </p>
            )}
          </div>
        </div>
      )}

      {/* Last Updated */}
      <div className="px-6 py-2 border-t border-gray-700 bg-gray-900">
        <div className="flex items-center justify-between text-xs text-gray-400">
          <span>Last updated</span>
          <span>{new Date(healthReport.timestamp).toLocaleTimeString()}</span>
        </div>
      </div>
    </div>
  );
};